
/**
 * ColorCardManager - Handles color card rendering and selection
 */
class ColorCardManager {
  constructor(options = {}) {
    this.colorCardsContainer = options.colorCardsContainer;
    this.colorCardsTitle = options.colorCardsTitle;
    this.input = options.input;
    this.productType = options.productType;
    this.colorCardData = [];
    this.selectedColorCard = null;
    this.onCardSelect = options.onCardSelect || (() => {});
    this.inputShownOnce = false; // Flag to track if input was shown once
    this.uploadedImageBase64 = null; // Store base64 image data
    
    // Upload section elements
    this.uploadSection = document.querySelector('[data-upload-section]');
    this.imageUploadInput = document.querySelector('[data-image-upload]');
    this.uploadPreview = document.querySelector('[data-upload-preview]');
    this.previewImg = document.querySelector('[data-preview-img]');
    this.uploadRemoveBtn = document.querySelector('[data-upload-remove]');
    
    this.initializeUploadHandlers();
  }

  setColorCardData(data) {
    this.colorCardData = data;
  }

  getDefaultColorCards() {
    console.log('[ColorCardManager] Providing default color card data.');
    return [
      {
        name: "Terracota do Nordeste",
        colors: ["#914525", "#DBC5AE", "#C79268"],
        prompts: { "input1": "terracotta", "input2": "warm beige", "input3": "burnt sienna" }
      },
      {
        name: "Doce Aurora",
        colors: ["#FFC0CB", "#E6E6FA", "#ADD8E6"],
        prompts: { "input1": "soft pink", "input2": "lavender mist", "input3": "baby blue sky" }
      },
      {
        name: "Verde Esmeralda",
        colors: ["#009B77"],
        prompts: { "input1": "deep emerald green jewel tones" }
      },
      {
        name: "Azul Oceano",
        colors: ["#00A7E1", "#48BFE3", "#80C4E8", "#B2D9EC"],
        prompts: { "input1": "ocean blue", "input2": "seafoam green", "input3": "aqua marine", "input4": "turquoise" }
      }
    ];
  }

  validateColorCardData(cardsArray) {
    if (!Array.isArray(cardsArray)) {
      console.warn('[ColorCardManager ValidateCards] Input is not an array. Received:', cardsArray);
      return [];
    }
    
    console.log(`[ColorCardManager ValidateCards] Validating array of ${cardsArray.length} potential card items.`);
    
    return cardsArray.map((card, index) => {
      console.log(`[ColorCardManager ValidateCards #${index}] --- Validating Card --- Raw data:`, JSON.stringify(card));
      
      if (!card || typeof card !== 'object') {
        console.warn(`[ColorCardManager ValidateCards #${index}] Item is not a valid object or is null. Skipping.`);
        return null;
      }
      

      // Ensure card has a name
      if (typeof card.name !== 'string' || !card.name.trim()) {
        console.warn(`[ColorCardManager ValidateCards #${index}] Invalid or missing name. Name:`, card.name);
        return null;
      }
      
      // Different validation based on product type
      if (this.productType === 'image_card' || this.productType === 'image_card_with_upload') {
        // For image_card and image_card_with_upload, validate image_url
        if (typeof card.image_url !== 'string' || !card.image_url.trim()) {
          console.warn(`[ColorCardManager ValidateCards #${index}] Invalid or missing image_url for image_card "${card.name}". image_url:`, card.image_url);
          return null;
        }
        
        const validCard = {
          name: card.name.trim(),
          image_url: card.image_url.trim()
        };
        
        // Preserve display_name if present
        if (card.display_name && typeof card.display_name === 'string') {
          validCard.display_name = card.display_name.trim();
        }
        
        console.log(`[ColorCardManager ValidateCards #${index}] Image card "${validCard.name}" is VALID. Processed data:`, JSON.stringify(validCard));
        return validCard;
      } else {
        // For color_card, validate colors
        if (!Array.isArray(card.colors) || card.colors.length === 0) {
          console.warn(`[ColorCardManager ValidateCards #${index}] Invalid or missing colors array for card "${card.name}". Colors:`, card.colors);
          return null;
        }
        
        // Validate each color is a proper hex code or color name (string)
        const validColors = card.colors.filter(color => {
          const isValid = typeof color === 'string' && color.trim() !== '';
          if (!isValid) {
            console.warn(`[ColorCardManager ValidateCards #${index}] Invalid color item in card "${card.name}". Color:`, color);
          }
          return isValid;
        });
        
        if (validColors.length === 0) {
          console.warn(`[ColorCardManager ValidateCards #${index}] Card "${card.name}" has no valid colors after filtering.`);
          return null;
        }
        
        // Create a clean card object
        const validCard = {
          name: card.name.trim(),
          colors: validColors
        };
        
        // Preserve display_name if present
        if (card.display_name && typeof card.display_name === 'string') {
          validCard.display_name = card.display_name.trim();
        }
        
        // Include prompts if present and valid
        if (card.prompts && typeof card.prompts === 'object' && Object.keys(card.prompts).length > 0) {
          validCard.prompts = card.prompts;
        }
        
        // Preserve display_name if present for color cards too
        if (card.display_name && typeof card.display_name === 'string') {
          validCard.display_name = card.display_name.trim();
        }
        
        console.log(`[ColorCardManager ValidateCards #${index}] Color card "${validCard.name}" is VALID. Processed data:`, JSON.stringify(validCard));
        return validCard;
      }
    }).filter(card => card !== null);
  }

  /**
   * Initialize upload handlers for image_card_with_upload
   */
  initializeUploadHandlers() {
    if (this.productType !== 'image_card_with_upload') return;
    
    if (this.imageUploadInput) {
      this.imageUploadInput.addEventListener('change', (e) => this.handleImageUpload(e));
    }
    
    if (this.uploadRemoveBtn) {
      this.uploadRemoveBtn.addEventListener('click', () => this.removeUploadedImage());
    }
  }

  /**
   * Handle image upload and convert to base64 with EXIF orientation correction
   */
  async handleImageUpload(event) {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.warn('[ColorCardManager] Invalid file type. Only images are allowed.');
      this.showToast(window.t('toasts.invalid_file_type'), 'error');
      return;
    }
    
    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      console.warn('[ColorCardManager] File too large:', file.size);
      this.showToast(window.t('toasts.file_too_large'), 'error');
      return;
    }
    
    let processedFile = file;
    
    // Apply EXIF orientation correction
    try {
      if (window.ImageExifUtils) {
        console.log('[ColorCardManager] Applying EXIF orientation correction...');
        this.showToast(window.t('toasts.processing_orientation'), 'info');
        
        processedFile = await window.ImageExifUtils.processImageOrientation(file, 0.98);
        console.log('[ColorCardManager] EXIF orientation correction completed');
      } else {
        console.warn('[ColorCardManager] ImageExifUtils not available, skipping orientation correction');
      }
    } catch (error) {
      console.error('[ColorCardManager] EXIF orientation correction error:', error);
      // Continue with the original file
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        this.uploadedImageBase64 = e.target.result; // Store full data URL
        
        if (this.previewImg && this.uploadPreview) {
          this.previewImg.src = e.target.result;
          this.uploadPreview.classList.remove('hidden');
          console.log('[ColorCardManager] Image preview shown and base64 stored (with orientation correction)');
        }
        
        this.showToast(window.t('toasts.image_loaded_success'), 'success');
      } catch (error) {
        console.error('[ColorCardManager] Error processing image:', error);
        this.showToast(window.t('toasts.image_process_error'), 'error');
      }
    };
    
    reader.onerror = () => {
      console.error('[ColorCardManager] Error reading file');
      this.showToast(window.t('toasts.file_read_error'), 'error');
    };
    
    reader.readAsDataURL(processedFile);
    console.log('[ColorCardManager] Processing image upload with orientation correction:', file.name);
  }

  /**
   * Remove uploaded image and clear base64 data
   */
  removeUploadedImage() {
    if (this.imageUploadInput) {
      this.imageUploadInput.value = '';
    }
    if (this.uploadPreview) {
      this.uploadPreview.classList.add('hidden');
    }
    if (this.previewImg) {
      this.previewImg.src = '';
    }
    
    this.uploadedImageBase64 = null; // Clear stored base64 data
    console.log('[ColorCardManager] Uploaded image removed and base64 cleared');
  }

  /**
   * Get uploaded image base64 data for generation
   */
  getUploadedImageBase64() {
    return this.uploadedImageBase64;
  }

  /**
   * Show upload section
   */
  showUploadSection() {
    if (this.uploadSection) {
      this.uploadSection.classList.remove('hidden');
      console.log('[ColorCardManager] Upload section shown');
    }
  }

  /**
   * Hide upload section
   */
  hideUploadSection() {
    if (this.uploadSection) {
      this.uploadSection.classList.add('hidden');
      console.log('[ColorCardManager] Upload section hidden');
    }
  }

  /**
   * Show toast notification
   */
  showToast(message, type = 'info') {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = `letz-ai__toast letz-ai__toast--${type}`;
    toast.textContent = message;
    
    // Find or create toast container
    let toastContainer = document.querySelector('.letz-ai__toast-container');
    if (!toastContainer) {
      toastContainer = document.createElement('div');
      toastContainer.className = 'letz-ai__toast-container';
      document.body.appendChild(toastContainer);
    }
    
    toastContainer.appendChild(toast);
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      if (toast.parentElement) {
        toast.classList.add('letz-ai__toast--fade-out');
        setTimeout(() => toast.remove(), 300);
      }
    }, 3000);
  }

  /**
   * Lock input when filled for color_card and image_card products
   */
  lockInputWhenFilled() {
    if ((this.productType === 'color_card' || this.productType === 'image_card' || this.productType === 'image_card_with_upload') && this.input && this.input.value.trim()) {
      this.input.disabled = true;
      this.input.style.cursor = 'not-allowed';
      this.input.style.pointerEvents = 'none';
      console.log('[ColorCardManager] Input locked - field has content');
    }
  }

  /**
   * Unlock input for editing
   */
  unlockInputForEditing() {
    if ((this.productType === 'color_card' || this.productType === 'image_card' || this.productType === 'image_card_with_upload') && this.input) {
      this.input.disabled = false;
      this.input.style.cursor = 'text';
      this.input.style.pointerEvents = 'auto';
      console.log('[ColorCardManager] Input unlocked for editing');
    }
  }

  /**
   * Set input value and lock it for copy prompt functionality
   * This method is specifically for copy prompt and doesn't clear the input
   */
  setInputValueAndLock(value) {
    if (this.input && (this.productType === 'color_card' || this.productType === 'image_card' || this.productType === 'image_card_with_upload')) {
      this.input.value = value;
      this.updateFieldContentClass();
      this.lockInputWhenFilled();
      console.log('[ColorCardManager] Input value set and locked for copy prompt:', value);
    }
  }

  /**
   * Clear input value and update field content class
   */
  clearInput() {
    if (this.input) {
      // Only unlock if the input actually has content to clear
      if ((this.productType === 'color_card' || this.productType === 'image_card' || this.productType === 'image_card_with_upload') && this.input.value.trim()) {
        this.unlockInputForEditing();
      }
      this.input.value = '';
      this.updateFieldContentClass();
      console.log('[ColorCardManager] Input cleared and field content class updated');
    }
    this.selectedColorCard = null;
  }

  /**
   * Update the has-content class on the field container based on input value
   */
  updateFieldContentClass() {
    if (!this.input) return;
    
    const fieldContainer = this.input.closest('.field.relative');
    if (!fieldContainer) return;
    
    if (this.input.value.trim()) {
      fieldContainer.classList.add('has-content');
      console.log('[ColorCardManager] Added has-content class to field container');
    } else {
      fieldContainer.classList.remove('has-content');
      console.log('[ColorCardManager] Removed has-content class from field container');
    }
  }

  /**
   * Find the input field container for hiding/showing
   */
  getInputFieldContainer() {
    if (!this.input) return null;
    
    // Look for the field container that wraps the input
    return this.input.closest('.field.relative');
  }

  /**
   * Show global loading overlay
   */
  showGlobalLoading() {
    const globalLoading = document.querySelector('[data-global-loading]');
    if (globalLoading) {
      globalLoading.classList.remove('hidden');
      console.log('[ColorCardManager] Global loading shown');
    }
  }

  /**
   * Hide global loading overlay
   */
  hideGlobalLoading() {
    const globalLoading = document.querySelector('[data-global-loading]');
    if (globalLoading) {
      globalLoading.classList.add('hidden');
      console.log('[ColorCardManager] Global loading hidden');
    }
  }

  initUIBasedOnProductType() {
    console.log(`[ColorCardManager] Initializing UI. ProductType: "${this.productType}"`);

    // Show global loading at the start
    this.showGlobalLoading();

    // Clear input first to ensure it starts empty
    this.clearInput();

    // Handle upload section based on product type
    if (this.productType === 'image_card_with_upload') {
      this.showUploadSection();
    } else {
      this.hideUploadSection();
    }

    // Manage input state based on product type
    if (this.input) {
      if (this.productType === 'color_card' || this.productType === 'image_card' || this.productType === 'image_card_with_upload') {
        this.input.disabled = true;
        this.input.placeholder = 'Selecione uma combinação';
        
        // Hide the input field completely for card product types
        const inputContainer = this.getInputFieldContainer();
        if (inputContainer) {
          inputContainer.classList.add('letz-ai__input-hidden');
          console.log(`[ColorCardManager] Input field hidden for ${this.productType} type.`);
        }
      } else {
        this.input.disabled = false;
        
        // Ensure input is visible for non-card types
        const inputContainer = this.getInputFieldContainer();
        if (inputContainer) {
          inputContainer.classList.remove('letz-ai__input-hidden');
          console.log('[ColorCardManager] Input field shown for non-card type.');
        }
        
        console.log('[ColorCardManager] Input enabled for non-card type.');
      }
      
      // Update field content class based on current input value
      this.updateFieldContentClass();
    }

    // Initialize container as empty to ensure no space is taken
    if (this.colorCardsContainer) {
      this.colorCardsContainer.classList.add('empty');
      console.log('[ColorCardManager] Container initialized as empty.');
    }

    // Ensure title is always hidden initially
    if (this.colorCardsTitle) {
      this.colorCardsTitle.classList.add('hidden');
      this.colorCardsTitle.style.display = 'none';
      console.log('[ColorCardManager] Title hidden during initialization.');
    }

    // Hide the wrapper div initially
    const wrapperDiv = document.querySelector('[data-color-cards-wrapper]');
    if (wrapperDiv) {
      wrapperDiv.classList.add('letz-ai__wrapper-hidden');
      console.log('[ColorCardManager] Wrapper div hidden during initialization.');
    }
  }

  renderColorCards() {
    if (!this.colorCardsContainer) {
      console.warn('[ColorCardManager RenderCards] No color cards container found. Skipping render.');
      return;
    }
    
    const wrapperDiv = document.querySelector('[data-color-cards-wrapper]');
    
    if (!this.colorCardData || this.colorCardData.length === 0) {
      // Keep container empty and hidden
      this.colorCardsContainer.classList.add('empty');
      this.colorCardsContainer.innerHTML = '';
      
      if (this.colorCardsTitle) {
        this.colorCardsTitle.classList.add('hidden');
        this.colorCardsTitle.style.display = 'none';
      }
      
      // Hide the wrapper div
      if (wrapperDiv) {
        wrapperDiv.classList.add('letz-ai__wrapper-hidden');
      }
      
      console.warn('[ColorCardManager RenderCards] No color card data available. Container and wrapper kept empty and hidden.');
      
      // For default products, check if loading should be hidden
      if (this.productType !== 'color_card' && this.productType !== 'image_card' && this.productType !== 'image_card_with_upload') {
        const imageGenerator = window.imageGeneratorInstance;
        if (imageGenerator) {
          imageGenerator.checkAndHideGlobalLoading();
        }
      }
      
      return;
    }
    
    console.log(`[ColorCardManager RenderCards] Starting to render ${this.colorCardData.length} cards for product type: ${this.productType}.`);
    
    this.colorCardsContainer.innerHTML = ''; 

    this.colorCardData.forEach((cardData, index) => {
      console.log(`[ColorCardManager RenderCards #${index}] Processing Card: "${cardData.name}" for type: ${this.productType}`);
      
      if (!cardData || typeof cardData.name !== 'string' || !cardData.name.trim()) {
        console.warn(`[ColorCardManager RenderCards #${index}] Invalid cardData. Skipping.`);
        return; 
      }

      const cardElement = document.createElement('div');
      
      // Set different classes based on product type
      if (this.productType === 'image_card' || this.productType === 'image_card_with_upload') {
        cardElement.className = 'letz-ai__image-card';
        
        // CRITICAL: Create separate image area element for background image
        const imageAreaElement = document.createElement('div');
        imageAreaElement.className = 'letz-ai__image-card-image';
        
        // FIXED: Apply background image to the image area element with proper validation
        if (cardData.image_url && typeof cardData.image_url === 'string' && cardData.image_url.trim()) {
          console.log(`[ColorCardManager RenderCards #${index}] Setting background image for "${cardData.name}": ${cardData.image_url}`);
          
          // Apply the background image to the image area element with all necessary properties
          const imageUrl = cardData.image_url.trim();
          imageAreaElement.style.backgroundImage = `url("${imageUrl}")`;
          imageAreaElement.style.backgroundSize = 'cover';
          imageAreaElement.style.backgroundPosition = 'center';
          imageAreaElement.style.backgroundRepeat = 'no-repeat';
          
          // ENSURE VISIBILITY - Force display properties
          imageAreaElement.style.display = 'block';
          imageAreaElement.style.visibility = 'visible';
          imageAreaElement.style.opacity = '1';
          
          console.log(`[ColorCardManager RenderCards #${index}] Background image applied successfully for "${cardData.name}"`, {
            backgroundImage: imageAreaElement.style.backgroundImage,
            backgroundSize: imageAreaElement.style.backgroundSize,
            backgroundPosition: imageAreaElement.style.backgroundPosition,
            display: imageAreaElement.style.display,
            visibility: imageAreaElement.style.visibility
          });
        } else {
          console.warn(`[ColorCardManager RenderCards #${index}] No valid image_url provided for image card "${cardData.name}"`, cardData.image_url);
          // Set a fallback background color
          imageAreaElement.style.backgroundColor = '#333';
          imageAreaElement.style.display = 'block';
          imageAreaElement.style.visibility = 'visible';
          imageAreaElement.style.opacity = '1';
        }
        
        // Append the image area to the card FIRST
        cardElement.appendChild(imageAreaElement);
        
        console.log(`[ColorCardManager RenderCards #${index}] Image area element created and appended for "${cardData.name}"`);
      } else {
        cardElement.className = 'letz-ai__color-card';
        
        // Set gradient for color cards (existing logic)
        if (cardData.colors && Array.isArray(cardData.colors)) {
          if (window.ColorCardUtils && window.ColorCardUtils.createGradientCSS && window.ColorCardUtils.applyGradientToCard) {
            console.log(`[ColorCardManager RenderCards #${index}] Using ColorCardUtils for "${cardData.name}"`);
            const gradientCSS = window.ColorCardUtils.createGradientCSS(cardData.colors);
            window.ColorCardUtils.applyGradientToCard(cardElement, gradientCSS);
          } else {
            console.warn(`[ColorCardManager RenderCards #${index}] ColorCardUtils not available, using fallback for "${cardData.name}"`);
            // Fallback to manual gradient creation
            const numColors = cardData.colors.length;
            let gradientCss = '';

            if (numColors === 1) {
              gradientCss = cardData.colors[0];
            } else if (numColors > 0) {
              const stops = cardData.colors.map((color, i) => {
                const percentage = (i / (numColors - 1)) * 100;
                return `${color} ${percentage}%`;
              }).join(', ');
              gradientCss = `linear-gradient(90deg, ${stops})`;
            } else {
              gradientCss = '#333';
            }
            
            if (gradientCss.startsWith('linear-gradient') || gradientCss.startsWith('radial-gradient')) {
              cardElement.style.background = gradientCss;
            } else {
              cardElement.style.backgroundColor = gradientCss;
            }
          }
        }
      }

      cardElement.setAttribute('role', 'button');
      cardElement.setAttribute('tabindex', '0');
      // Use display_name for accessibility, fallback to name if display_name doesn't exist
      const displayName = cardData.display_name || cardData.name;
      cardElement.setAttribute('aria-label', `Selecionar: ${displayName}`);

      const nameElement = document.createElement('div');
      nameElement.className = (this.productType === 'image_card' || this.productType === 'image_card_with_upload') ? 'letz-ai__image-card-name' : 'letz-ai__color-card-name';
      // Use display_name for showing to user, fallback to name if display_name doesn't exist
      nameElement.textContent = displayName;

      cardElement.appendChild(nameElement);

      cardElement.addEventListener('click', () => this.handleColorCardSelect(cardData, cardElement));
      cardElement.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleColorCardSelect(cardData, cardElement);
        }
      });

      this.colorCardsContainer.appendChild(cardElement);
      
      // ENHANCED DEBUG: Log the final card element structure for image cards
      if (this.productType === 'image_card' || this.productType === 'image_card_with_upload') {
        const imageArea = cardElement.querySelector('.letz-ai__image-card-image');
        console.log(`[ColorCardManager RenderCards #${index}] FINAL DEBUG - Image card structure for "${cardData.name}":`, {
          cardElement: {
            className: cardElement.className,
            childrenCount: cardElement.children.length,
            children: Array.from(cardElement.children).map(child => child.className)
          },
          imageArea: imageArea ? {
            className: imageArea.className,
            backgroundImage: imageArea.style.backgroundImage,
            backgroundSize: imageArea.style.backgroundSize,
            backgroundPosition: imageArea.style.backgroundPosition,
            display: imageArea.style.display,
            visibility: imageArea.style.visibility,
            opacity: imageArea.style.opacity,
            width: imageArea.style.width,
            height: imageArea.style.height
          } : 'IMAGE AREA NOT FOUND - THIS IS THE PROBLEM!',
          nameElement: {
            className: nameElement.className,
            textContent: nameElement.textContent
          }
        });
        
        // Additional check to ensure image area is properly attached
        if (!imageArea) {
          console.error(`[ColorCardManager RenderCards #${index}] CRITICAL ERROR: Image area element not found in DOM for "${cardData.name}"`);
        }
      }
    });
    
    // Show title, container and wrapper only after cards are successfully rendered
    if (this.colorCardData && this.colorCardData.length > 0) {
      // Remove empty class and show container
      this.colorCardsContainer.classList.remove('empty');
      console.log('[ColorCardManager] Container shown after cards rendered successfully.');
      
      // Show title
      if (this.colorCardsTitle) {
        this.colorCardsTitle.classList.remove('hidden');
        this.colorCardsTitle.style.display = 'block';
        console.log('[ColorCardManager] Title shown after cards rendered successfully.');
      }
      
      // Show the wrapper div
      if (wrapperDiv) {
        wrapperDiv.classList.remove('letz-ai__wrapper-hidden');
        console.log('[ColorCardManager] Wrapper div shown after cards rendered successfully.');
      }
      
      // DO NOT auto-select the first card - always start with empty input
      console.log('[ColorCardManager] Cards rendered but no auto-selection performed - input remains empty');
      
      // For default products, check if loading should be hidden
      if (this.productType !== 'color_card' && this.productType !== 'image_card' && this.productType !== 'image_card_with_upload') {
        const imageGenerator = window.imageGeneratorInstance;
        if (imageGenerator) {
          imageGenerator.checkAndHideGlobalLoading();
        }
      }
    }
    
    // Apply dynamic height calculation with improved timing (for both color and image cards)
    if (this.productType === 'color_card' || this.productType === 'image_card' || this.productType === 'image_card_with_upload') {
      this.applyDynamicHeight();
    }
    
    console.log('[ColorCardManager RenderCards] Finished rendering all cards.');
  }

  applyDynamicHeight() {
    // Wait for ColorCardUtils to be available with retry mechanism
    const tryApplyHeight = (attempts = 0) => {
      const maxAttempts = 5;
      
      if (window.ColorCardUtils) {
        console.log('[ColorCardManager] Applying dynamic height calculation...');
        const cardNames = this.colorCardData.map(card => card.name);
        
        if (this.productType === 'color_card') {
          // Use existing color card logic
          if (window.ColorCardUtils.calculateOptimalCardHeight && window.ColorCardUtils.applyHeightWithDelay) {
            const optimalHeight = window.ColorCardUtils.calculateOptimalCardHeight(cardNames);
            window.ColorCardUtils.applyHeightWithDelay(optimalHeight, 100);
            console.log(`[ColorCardManager] Dynamic height applied to color cards: ${optimalHeight}px`);
          }
        } else if (this.productType === 'image_card' || this.productType === 'image_card_with_upload') {
          // Use new image card logic
          if (window.ColorCardUtils.calculateOptimalImageCardHeight && window.ColorCardUtils.applyImageCardHeightWithDelay) {
            const optimalHeight = window.ColorCardUtils.calculateOptimalImageCardHeight(cardNames);
            window.ColorCardUtils.applyImageCardHeightWithDelay(optimalHeight, 100);
            console.log(`[ColorCardManager] Dynamic height applied to image cards: ${optimalHeight}px`);
          }
        }
      } else if (attempts < maxAttempts) {
        console.log(`[ColorCardManager] ColorCardUtils not ready, retrying... (${attempts + 1}/${maxAttempts})`);
        setTimeout(() => tryApplyHeight(attempts + 1), 100);
      } else {
        console.warn('[ColorCardManager] ColorCardUtils height functions not available after retries, using default CSS height');
      }
    };

    // Start trying to apply height
    tryApplyHeight();
  }

  handleColorCardSelect(cardData, cardElement) {
    console.log('[ColorCardManager] handleColorCardSelect called with:', {
      cardName: cardData.name,
      cardElement: cardElement,
      elementClasses: cardElement.className,
      containerExists: !!this.colorCardsContainer
    });

    if (!this.colorCardsContainer) {
      console.warn('[ColorCardManager] Cannot handle card select - container not found.');
      return;
    }

    // Remove selection from all cards (both color and image cards) with improved selector
    const allCards = this.colorCardsContainer.querySelectorAll('.letz-ai__color-card, .letz-ai__image-card');
    console.log('[ColorCardManager] Found cards to deselect:', allCards.length);
    
    allCards.forEach((card, index) => {
      if (card.classList.contains('selected')) {
        console.log(`[ColorCardManager] Removing selection from card ${index}:`, card.className);
        card.classList.remove('selected');
      }
    });

    // Add selection to clicked card with validation
    if (cardElement) {
      console.log('[ColorCardManager] Adding selection to card:', cardElement.className);
      cardElement.classList.add('selected');
      
      // Verify the class was added
      setTimeout(() => {
        const hasSelected = cardElement.classList.contains('selected');
        console.log('[ColorCardManager] Selection verification - has selected class:', hasSelected);
        console.log('[ColorCardManager] Final card classes:', cardElement.className);
        
        // Force a style recalculation
        cardElement.offsetHeight;
      }, 50);
    }

    this.selectedColorCard = cardData;
    
    console.log(`[DEBUG LOADING] Card selected for productType "${this.productType}":`, {
      cardName: cardData.name,
      selectedColorCard: this.selectedColorCard,
      hasSelectedCard: !!this.selectedColorCard
    });
    
    console.log('[ColorCardManager] handleColorCardSelect called with:', cardData);
    console.log('[ColorCardManager] this.input exists:', !!this.input);
    console.log('[ColorCardManager] this.input element:', this.input);
    
    if (this.input) {
      const displayValue = cardData.display_name || cardData.name;
      console.log('[ColorCardManager] Setting input value to:', displayValue);
      
      this.input.value = displayValue;
      console.log('[ColorCardManager] Input value after setting:', this.input.value);
      console.log(`[ColorCardManager] Card selected: "${cardData.name}" (ID: ${cardData.name}, Display: ${cardData.display_name || cardData.name})`);
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      
      // Update field content class after setting the value
      this.updateFieldContentClass();
      
      // Show input field if product type is color_card, image_card, or image_card_with_upload
      if (this.productType === 'color_card' || this.productType === 'image_card' || this.productType === 'image_card_with_upload') {
        const inputContainer = this.getInputFieldContainer();
        if (inputContainer) {
          inputContainer.classList.remove('letz-ai__input-hidden');
          this.input.disabled = false;
          this.inputShownOnce = true; // Mark as shown once
          console.log('[ColorCardManager] Input field shown after card selection.');
          
          // Lock input after filling
          this.lockInputWhenFilled();
        }
      }
    }

    // Notify parent component
    this.onCardSelect(cardData);
    console.log('[ColorCardManager] Card selected:', cardData.name);
  }

  getSelectedColorCard() {
    console.log('[DEBUG LOADING] getSelectedColorCard called, returning:', this.selectedColorCard);
    return this.selectedColorCard;
  }

  deselectColorCard() {
    if (!this.colorCardsContainer) {
      return;
    }
    
    // Remove selection from all cards (both color and image cards)
    const previouslySelected = this.colorCardsContainer.querySelector('.letz-ai__color-card.selected, .letz-ai__image-card.selected');
    if (previouslySelected) {
      previouslySelected.classList.remove('selected');
    }
    this.selectedColorCard = null;
    
    // Clear input and update field content class
    this.clearInput();
    
    // Don't hide input field if it was shown once for card products
    if ((this.productType === 'color_card' || this.productType === 'image_card' || this.productType === 'image_card_with_upload') && !this.inputShownOnce) {
      const inputContainer = this.getInputFieldContainer();
      if (inputContainer) {
        inputContainer.classList.add('letz-ai__input-hidden');
        console.log('[ColorCardManager] Input field hidden after deselecting card.');
      }
    }
  }

  /**
   * Public method to select a card by name - used for auto-selection when copying prompts
   */
  selectCardByName(cardName) {
    console.log('[ColorCardManager] selectCardByName called with:', cardName);
    
    if (!this.colorCardsContainer || !cardName) {
      console.warn('[ColorCardManager] Cannot select card - missing container or cardName');
      return false;
    }

    // Find the card data first
    const cardData = this.colorCardData.find(card => 
      card.name && card.name.toLowerCase().trim() === cardName.toLowerCase().trim()
    );

    if (!cardData) {
      console.warn('[ColorCardManager] Card data not found for name:', cardName);
      return false;
    }

    // Find the corresponding DOM element
    const allCards = this.colorCardsContainer.querySelectorAll('.letz-ai__color-card, .letz-ai__image-card');
    let targetCardElement = null;

    allCards.forEach(cardElement => {
      const nameElement = cardElement.querySelector('.letz-ai__color-card-name, .letz-ai__image-card-name');
      if (nameElement) {
        const textContent = nameElement.textContent.toLowerCase().trim();
        const cardName = cardData.name?.toLowerCase().trim();
        const cardDisplayName = cardData.display_name?.toLowerCase().trim();
        if (textContent === cardName || textContent === cardDisplayName) {
          targetCardElement = cardElement;
        }
      }
    });

    if (targetCardElement) {
      console.log('[ColorCardManager] Found card element, triggering selection');
      this.handleColorCardSelect(cardData, targetCardElement);
      return true;
    } else {
      console.warn('[ColorCardManager] Card element not found in DOM for name:', cardName);
      return false;
    }
  }
}

// Export the class globally
window.ColorCardManager = ColorCardManager;
